import React, { useState, useMemo, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, Save, Copy, CheckCircle2, Wrench, Info, Layers } from "lucide-react";
import { BCM_STYLE as S } from "../styles/BookingCalendarStyle";
import InstrumentLoader from "./InstrumentLoader";
import { createPortal } from "react-dom";
import { AircraftRentalDetailsDto, CalendarPayloadDto, TimeSlotDto, TimePointDto } from "@/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (rentalDetails: AircraftRentalDetailsDto) => void;
  registration: string;
  intervalStep?: number;
  initialRentalDetails?: AircraftRentalDetailsDto;
}

type SlotStateMap = Record<number, 'FREE_FOR_RENT' | 'BLOCKED_BY_USER'>;

const AircraftRentalCalendarModal = ({ isOpen, onClose, onSave, registration, intervalStep = 30, initialRentalDetails }: Props) => {
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [dailySlots, setDailySlots] = useState<Record<string, SlotStateMap>>({});
  const [activeMarkingType, setActiveMarkingType] = useState<'FREE_FOR_RENT' | 'BLOCKED_BY_USER'>('FREE_FOR_RENT');
  const [localLoading, setLocalLoading] = useState(false);
  const [slotToUuidMap, setSlotToUuidMap] = useState<Record<string, Record<number, string>>>({});
  const [removedUuids, setRemovedUuids] = useState<string[]>([]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const rows = [
    { label: "Dimineață", start: 6, end: 10 },
    { label: "Prânz", start: 10, end: 14 },
    { label: "După-amiază", start: 14, end: 18 }
  ];

  const allDaySlotIds = useMemo(() => {
    const slots: number[] = [];
    for (let offset = 0; offset < (12 * 60); offset += intervalStep) {
      slots.push(6 * 60 + offset);
    }
    return slots;
  }, [intervalStep]);

  useEffect(() => {
    if (isOpen) {
      const timeSlotsList = initialRentalDetails?.calendarPayloadDto?.timeSlotDtoList;
      if (timeSlotsList && timeSlotsList.length > 0) {
        const hydratedSlots: Record<string, SlotStateMap> = {};
        const hydratedUuidMap: Record<string, Record<number, string>> = {};
        const hydratedDates = new Set<string>();

        timeSlotsList.forEach((slotDto) => {
          if (!slotDto.from || !slotDto.to) return;

          const fromYear = slotDto.from.year ?? today.getFullYear();
          const fromMonth = (slotDto.from.month ?? 1) - 1;
          const fromDay = slotDto.from.day ?? 1;
          const fromHour = slotDto.from.hour ?? 0;
          const fromMinute = slotDto.from.minute ?? 0;

          const toHour = slotDto.to.hour ?? 0;
          const toMinute = slotDto.to.minute ?? 0;

          const startDate = new Date(fromYear, fromMonth, fromDay, 0, 0, 0, 0);
          const dateKey = startDate.toDateString();
          hydratedDates.add(dateKey);

          if (!hydratedSlots[dateKey]) hydratedSlots[dateKey] = {};
          if (!hydratedUuidMap[dateKey]) hydratedUuidMap[dateKey] = {};

          const startTotalMin = fromHour * 60 + fromMinute;
          const endTotalMin = toHour * 60 + toMinute;

          const slotTypeEnumMap: Record<number, 'FREE_FOR_RENT' | 'BLOCKED_BY_USER'> = {
            1: 'FREE_FOR_RENT',
            2: 'BLOCKED_BY_USER'
          };
          const mappedState = slotDto.timeSlotType !== undefined ? slotTypeEnumMap[slotDto.timeSlotType] || 'FREE_FOR_RENT' : 'FREE_FOR_RENT';

          for (let m = startTotalMin; m < endTotalMin; m += intervalStep) {
            hydratedSlots[dateKey][m] = mappedState;
            if (slotDto.uuid) {
              hydratedUuidMap[dateKey][m] = slotDto.uuid;
            }
          }
        });

        setDailySlots(hydratedSlots);
        setSlotToUuidMap(hydratedUuidMap);
        setSelectedDates(Array.from(hydratedDates));
        setRemovedUuids([]);

        if (hydratedDates.size > 0) {
          const dateArray = Array.from(hydratedDates);
          const firstFuture = dateArray.find(d => new Date(d) >= today) || dateArray[0];
          setActiveDate(firstFuture);
        }
      } else {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();

        const defaultMonthDates: string[] = [];
        const defaultDailySlots: Record<string, SlotStateMap> = {};

        for (let i = 1; i <= daysInCurrentMonth; i++) {
          const dayObj = new Date(year, month, i);
          dayObj.setHours(0, 0, 0, 0);

          if (dayObj >= today) {
            const dateStr = dayObj.toDateString();
            defaultMonthDates.push(dateStr);

            const daySlotsMap: SlotStateMap = {};
            allDaySlotIds.forEach(slotId => {
              daySlotsMap[slotId] = 'FREE_FOR_RENT';
            });
            defaultDailySlots[dateStr] = daySlotsMap;
          }
        }

        setDailySlots(defaultDailySlots);
        setSelectedDates(defaultMonthDates);
        setSlotToUuidMap({});
        setRemovedUuids([]);
        setIsMultiSelectMode(true);

        if (defaultMonthDates.length > 0) {
          setActiveDate(defaultMonthDates[0]);
        }
      }
    }
  }, [isOpen]);

  const timeSlots = useMemo(() => {
    const slots = [];
    for (let offset = 0; offset < (12 * 60); offset += intervalStep) {
      const totalMin = 6 * 60 + offset;
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      slots.push({ id: totalMin, label: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`, hour: h });
    }
    return slots;
  }, [intervalStep]);

  const toggleSlot = (slotId: number) => {
    if (!activeDate) return;

    setDailySlots(prev => {
      const dayMap = { ...(prev[activeDate] || {}) };
      const uuid = slotToUuidMap[activeDate]?.[slotId];

      if (dayMap[slotId]) {
        if (uuid) {
          setRemovedUuids(prevUuids => Array.from(new Set([...prevUuids, uuid])));
        }
        delete dayMap[slotId];
      } else {
        if (uuid) {
          setRemovedUuids(prevUuids => prevUuids.filter(u => u !== uuid));
        }
        dayMap[slotId] = activeMarkingType;
      }
      return { ...prev, [activeDate]: dayMap };
    });
  };

  const handleManualSelectMode = () => {
    setSelectedDates([]);
    setDailySlots({});
    setActiveDate(null);
    setIsMultiSelectMode(true);
  };

  const handleBulkSelect = (filterFn: (d: Date) => boolean) => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const newDatesInMonth: string[] = [];
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      if (filterFn(date) && date >= today) {
        newDatesInMonth.push(date.toDateString());
      }
    }

    setIsMultiSelectMode(true);
    setSelectedDates(prev => Array.from(new Set([...prev, ...newDatesInMonth])));
    if (newDatesInMonth.length > 0 && !activeDate) setActiveDate(newDatesInMonth[0]);
  };

  const handleDateClick = (date: Date) => {
    if (date < today) return;
    const dStr = date.toDateString();
    if (isMultiSelectMode) {
      setSelectedDates(prev => prev.includes(dStr) ? prev.filter(d => d !== dStr) : [...prev, dStr]);
      setActiveDate(dStr);
    } else {
      setSelectedDates([dStr]);
      setActiveDate(dStr);
    }
  };

  const applyToAllSelected = () => {
    if (!activeDate || selectedDates.length < 2) return;
    const sourceConfig = dailySlots[activeDate] || {};
    setDailySlots(prev => {
      const newDailySlots = { ...prev };
      selectedDates.forEach(dateStr => { newDailySlots[dateStr] = { ...sourceConfig }; });
      return newDailySlots;
    });
  };

  const handleSave = () => {
      const timeSlotDtoList: TimeSlotDto[] = [];

      Object.entries(dailySlots).forEach(([dateStr, slots]) => {
        const dayDate = new Date(dateStr);
        const year = dayDate.getFullYear();
        const month = dayDate.getMonth() + 1;
        const day = dayDate.getDate();

        // Parcurgem fiecare slot selectat individual (indiferent că e FREE_FOR_RENT sau BLOCKED_BY_USER)
        Object.entries(slots).forEach(([idStr, targetState]) => {
          const startMin = parseInt(idStr);
          const endMin = startMin + intervalStep;

          const startHour = Math.floor(startMin / 60);
          const startMinute = startMin % 60;

          const endHour = Math.floor(endMin / 60);
          const endMinute = endMin % 60;

          const timeSlotTypeEnumMap: Record<string, number> = {
            'FREE_FOR_RENT': 1,
            'BLOCKED_BY_USER': 2
          };

          const fromPoint: TimePointDto = {
            timePointType: 1,
            year,
            month,
            day,
            hour: startHour,
            minute: startMinute
          };

          const toPoint: TimePointDto = {
            timePointType: 2,
            year,
            month,
            day,
            hour: endHour,
            minute: endMinute
          };

          timeSlotDtoList.push({
            uuid: crypto.randomUUID ? crypto.randomUUID() : String(Math.random()),
            bookingId: 0,
            from: fromPoint,
            to: toPoint,
            timeSlotType: timeSlotTypeEnumMap[targetState] || 1
          });
        });
      });

      const payload: AircraftRentalDetailsDto = {
        ...initialRentalDetails,
        calendarPayloadDto: {
          timeSlotDtoList
        }
      };

      onSave(payload);
    };

  if (!isOpen) return null;

  const currentMonth = viewDate.getMonth();
  const currentYear = viewDate.getFullYear();
  const monthNames = ["IAN", "FEB", "MAR", "APR", "MAI", "IUN", "IUL", "AUG", "SEP", "OCT", "NOI", "DEC"];
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const adjustedFirstDay = new Date(currentYear, currentMonth, 1).getDay() === 0 ? 6 : new Date(currentYear, currentMonth, 1).getDay() - 1;

  return createPortal(
    <div className={S.overlay2}>
      <InstrumentLoader visible={localLoading} />
      <div className={S.modal2}>

        <div className={S.sidebarLeft}>
          <div className="flex items-center justify-between mb-1">
            <div className={S.stepHeader}>
              <div className={S.stepIconBlue}><CalendarIcon size={18} /></div>
              <div>
                <p className={S.stepLabel}>Calendar</p>
                <h3 className={S.stepTitle}>Disponibilitate</h3>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsMultiSelectMode(!isMultiSelectMode);
                if (isMultiSelectMode) setSelectedDates(activeDate ? [activeDate] : []);
              }}
              className={`p-2 rounded-xl transition-all border ${isMultiSelectMode ? 'bg-orange-500 border-orange-400 text-white shadow-[0_0_15px_rgba(242,108,21,0.4)]' : 'bg-slate-800/50 border-slate-700 text-slate-500 hover:text-slate-300'}`}
            >
              <Layers size={16} />
            </button>
          </div>

          <div className="mb-6 p-3 bg-blue-500/5 rounded-xl border border-blue-500/10">
            <p className="text-[9px] text-slate-400 uppercase tracking-tight leading-relaxed">
              Configurați timpul disponibil, apoi folosiți <span className="text-orange-500 font-bold">Sincronizare</span>.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-6">
            <button type="button" onClick={() => handleBulkSelect(() => true)} className="py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-[9px] font-black uppercase text-slate-400 hover:text-white transition-all">+ Toată luna</button>
            <button type="button" onClick={() => handleBulkSelect((d) => d.getDay() !== 0 && d.getDay() !== 6)} className="py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-[9px] font-black uppercase text-slate-400 hover:text-white transition-all">+ Luni-Vineri</button>
            <button
              type="button"
              onClick={handleManualSelectMode}
              className="col-span-2 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-[9px] font-black uppercase text-slate-400 hover:text-white hover:border-orange-500/40 transition-all"
            >
              Selectare manuală
            </button>
          </div>

          <div className={S.calendarNav}>
            <button type="button" onClick={() => setViewDate(new Date(currentYear, currentMonth - 1, 1))} className={S.calendarNavBtn} disabled={currentYear <= today.getFullYear() && currentMonth <= today.getMonth()}><ChevronLeft size={16} /></button>
            <span className={S.monthDisplay}>{monthNames[currentMonth]} {currentYear}</span>
            <button type="button" onClick={() => setViewDate(new Date(currentYear, currentMonth + 1, 1))} className={S.calendarNavBtn}><ChevronRight size={16} /></button>
          </div>

          <div className={S.calendarGrid}>
            {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map(h => <div key={h} className={S.weekdayLabel}>{h}</div>)}
            {Array.from({ length: adjustedFirstDay }).map((_, idx) => <div key={`spacer-${idx}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const dateObj = new Date(currentYear, currentMonth, i + 1);
              const dStr = dateObj.toDateString();
              const isSelected = selectedDates.includes(dStr);
              const isActive = activeDate === dStr;
              const hasConfig = dailySlots[dStr] && Object.keys(dailySlots[dStr]).length > 0;
              const isPast = dateObj < today;

              return (
                <button
                  type="button"
                  key={i}
                  disabled={isPast}
                  onClick={() => handleDateClick(dateObj)}
                  className={`relative ${S.dayButtonBase} ${isPast ? 'opacity-20 grayscale cursor-not-allowed' : ''} ${(isSelected && isMultiSelectMode) || (isActive && !isMultiSelectMode) ? S.dayButtonActive : S.dayButtonInactive} ${isActive && isMultiSelectMode ? 'ring-1 ring-orange-500' : ''}`}
                >
                  {i + 1}
                  {hasConfig && <span className={`absolute bottom-1 w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-orange-500'}`}></span>}
                </button>
              );
            })}
          </div>

          <div className="mt-auto pt-4">
            {isMultiSelectMode && selectedDates.length > 1 && (
              <button
                type="button"
                onClick={applyToAllSelected}
                className="w-full px-3 py-3 bg-orange-600/10 border border-orange-500/30 text-orange-500 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-orange-600 hover:text-white transition-all flex items-center justify-center gap-2 text-center"
              >
                <Copy className="shrink-0" size={14} />
                <span className="truncate">Sincronizează ({selectedDates.length} zile)</span>
              </button>
            )}
          </div>
        </div>

        <div className={S.mainContent}>
          <button type="button" onClick={onClose} className="absolute top-6 right-8 text-slate-500 hover:text-white transition-colors z-20"><X size={20} /></button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className={S.clockGlow}></div>
                <div className={S.clockIconBox}><Clock size={18} /></div>
              </div>
              <div>
                <p className={S.timelineHeaderLabel}>{registration}</p>
                <h3 className={S.timelineHeaderTitle}>{activeDate || 'Selectează o dată'}</h3>
              </div>
            </div>

            <div className="flex gap-1.5 bg-slate-900/80 p-1 rounded-xl border border-white/5 backdrop-blur-md">
              <button type="button" onClick={() => setActiveMarkingType('FREE_FOR_RENT')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${activeMarkingType === 'FREE_FOR_RENT' ? 'bg-orange-500 text-white' : 'text-slate-500 hover:text-slate-400'}`}>
                <CheckCircle2 size={12} /> Liber de închiriat
              </button>
              <button type="button" onClick={() => setActiveMarkingType('BLOCKED_BY_USER')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${activeMarkingType === 'BLOCKED_BY_USER' ? 'bg-red-600 text-white' : 'text-slate-500 hover:text-slate-400'}`}>
                <Wrench size={12} /> Blocat
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {!activeDate ? (
              <div className="h-[400px] flex flex-col items-center justify-center text-slate-700">
                <CalendarIcon className="mb-4 opacity-20" size={48} strokeWidth={1} />
                <p className="text-[10px] font-black uppercase tracking-[0.3em]">Așteptare selecție calendar</p>
              </div>
            ) : (
              <>
                {rows.map((row, idx) => (
                  <div key={idx} className="relative">
                    <div className="flex items-center gap-3 mb-6"><div className={S.rowDivider}></div></div>
                    <div className={S.slotsGrid}>
                      {timeSlots.filter(s => s.hour >= row.start && s.hour < row.end).map((slot) => {
                        const type = dailySlots[activeDate]?.[slot.id];
                        let stateKey: keyof typeof S.slotStates = 'default';
                        if (type === 'FREE_FOR_RENT') stateKey = 'selected';
                        if (type === 'BLOCKED_BY_USER') stateKey = 'maintenance';

                        return (
                          <button
                            type="button"
                            key={slot.id}
                            onClick={() => toggleSlot(slot.id)}
                            className={`${S.slotBase} ${S.slotStates[stateKey]} h-16 active:scale-95 transition-all`}
                          >
                            <span className={`text-[10px] font-mono font-bold ${type ? 'text-white' : 'text-slate-400'}`}>{slot.label}</span>
                            <span className={`text-[7px] font-black uppercase tracking-widest mt-1 ${type ? 'text-white/60' : 'text-slate-600'}`}>
                              {type === 'FREE_FOR_RENT' ? 'OPEN' : type === 'BLOCKED_BY_USER' ? 'BLOCKED' : 'FREE'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-slate-900 rounded-xl border border-white/5"><Info className="text-blue-500" size={16} /></div>
                    <div>
                      <p className={S.unitLabel}>Configurație curentă</p>
                      <p className="text-xs font-mono text-slate-400">{Object.keys(dailySlots).length} zile definite</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    <button type="button" onClick={onClose} className="px-8 py-4 text-[10px] font-black uppercase text-slate-500 hover:text-white transition-all">Anulează</button>
                    <button type="button" onClick={handleSave} disabled={Object.keys(dailySlots).length === 0} className={`${S.confirmBtnBase} !w-64 ${Object.keys(dailySlots).length > 0 ? S.confirmBtnActive : S.confirmBtnDisabled}`}>
                      Salvează Program <Save size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AircraftRentalCalendarModal;